/**
 * Testable component is a type that can be extended to centralize the configuration for a test ID prop
 */
export type TestableComponentProps = { testId?: string };

/**
 * TestIdAttr is a type for an object that contains a 'data-testid' attribute.
 */
export type TestIdAttr = { 'data-testid': string | undefined };

/**
 * Composes a test ID string from a base ID and an optional suffix.
 * This is useful for creating unique identifiers for testing purposes.
 * @example
 * ```tsx
 * // Compose a test ID to use elsewhere
 * const myTestId = getTestId('my-component', id); // current value of id is 'abc123
 * // myTestId will be 'my-component-abc123'
 * ```
 * @example
 * ```tsx
 * // Pass a composed test ID to a component that has a testID prop
 * <CustomComponent testId={getTestId('my-component', id)} />
 * ```
 * @param id the root ID for the test
 * @param suffix an optional suffix to append to the ID
 * @returns the composed test ID string
 */
export function getTestId(id: string, suffix?: string): string | undefined {
  if (!id) return undefined;
  return suffix ? `${id}-${suffix}` : id;
}

/**
 * Composes a test ID attribute object from a base ID and an optional suffix and outputs as an object
 * with an HTML-safe 'data-testid' property. This is useful for passing to React components.
 * @example
 * ```tsx
 * // Populate an HTML-safe test ID attribute onto a component
 * <div {...getTestIdAttr('my-component')}>Header Content</div>
 * ```
 * @param id the root ID for the test
 * @param suffix an optional suffix to append to the ID
 * @returns an object containing the composed test ID as a 'data-testid' attribute
 */
export function getTestIdAttr(id: string, suffix?: string): TestIdAttr {
  return { 'data-testid': getTestId(id, suffix) };
}
